<template>
  <BaseModalEditCreate
    :disable="mode != 'edit'"
    :title="isNew ? $t('common.actions.create') : $t('common.actions.edit')"
    :is-modified="isModified"
    max-width="wide"
    @dismiss-changes="emit('close')"
    @save="onSave">
    <template #form>
      <div v-if="modifiedData" class="w-full h-full overflow-auto">
        <table
          class="w-full h-full overflow-auto text-sm text-left text-gray-900 divide-y divide-gray-200">
          <tbody>
            <BaseDetailsTableRow :name="$t('traderSites.traderName')" :mode="mode">
              {{ traderName || '-' }}
            </BaseDetailsTableRow>
            <BaseDetailsTableRow :name="$t('baseObject.name')" :mode="mode">
              <BaseInput
                v-model="modifiedData.name"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>
            <BaseDetailsTableRow :name="$t('baseObject.code')" :mode="mode">
              <BaseInput
                v-model="modifiedData.code"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>
            <BaseDetailsTableRow :name="$t('traderSites.companySite')" :mode="mode">
              <BaseSelectDropdown
                :items="dropdownCompanySites"
                :selected-item="selectedCompanySite"
                @update:selected="handleCompanySiteSelection"
                name="companySites"
                :mode="mode"
                flat
                show-error-as-tooltip
                clearable
                :error="
                  $v.companySiteId.required.$invalid ? $v.companySiteId.required.$message : ''
                "
            /></BaseDetailsTableRow>
            <BaseDetailsTableRow :mode="mode" :name="$t('vatLevel')">
              <BaseSelectDropdown
                :mode="mode"
                name="vatLevel"
                flat
                :items="dropdownVatLevels"
                :selected-item="selectedVatLevel"
                clearable
                show-error-as-tooltip
                :error="$v.vatLevel.required.$invalid ? $v.vatLevel.required.$message : ''"
                @update:selected="handleVatLevelSelection" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('customer.currentTraderSiteTaxCode')" :mode="mode">
              <BaseInput
                v-model="modifiedData.aadeTaxCode"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('address')" :mode="mode">
              <BaseInput
                v-model="modifiedData.address"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('city')" :mode="mode">
              <BaseInput
                v-model="modifiedData.city"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>
            <BaseDetailsTableRow name="County" :mode="mode">
              <BaseInput
                v-model="modifiedData.county"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>
            <BaseDetailsTableRow name="ZipCode" :mode="mode">
              <BaseInput
                v-model="modifiedData.zipCode"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>
            <BaseDetailsTableRow name="Email" :mode="mode">
              <BaseInput
                v-model="modifiedData.email"
                color="base"
                :mode="mode"
                flat
                show-error-as-tooltip
                class="w-full"
                :error="$v.email.email.$invalid ? $v.email.email.$message : ''" />
            </BaseDetailsTableRow>
            <BaseDetailsTableRow :name="$t('telephone')" :mode="mode">
              <BaseInput
                v-model="modifiedData.telephone1"
                color="base"
                :mode="mode"
                flat
                class="w-full" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :mode="mode" :name="$t('traderSites.latitude')">
              <BaseInput
                :mode="mode"
                v-model="modifiedData.latitude"
                color="base"
                flat
                type="number"
                :disabled="mode == 'view'" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :mode="mode" :name="$t('traderSites.longitude')">
              <BaseInput
                :mode="mode"
                v-model="modifiedData.longitude"
                color="base"
                flat
                type="number"
                :disabled="mode == 'view'" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('baseObject.isActive')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="modifiedData.isActive ? modifiedData.isActive : 0" />
              <BaseToggle v-else name="isActive" v-model="modifiedData.isActive" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('HQ')" :mode="mode">
              <BaseTableCellCheck
                v-if="mode == 'view'"
                :item="modifiedData.isHQ ? modifiedData.isHQ : 0" />
              <BaseToggle v-else name="isHQ" v-model="modifiedData.isHQ" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('branch')" :mode="mode">
              <BaseTableCellCheck
                v-if="mode == 'view'"
                :item="modifiedData.isBranch ? modifiedData.isBranch : false" />
              <BaseToggle v-else name="isBranch" v-model="modifiedData.isBranch" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow
              :name="$t('traderSites.storePhoto')"
              :mode="mode"
              @dragstart.prevent
              @drop.prevent>
              <ImageUpload
                v-if="mode == 'edit'"
                :image="storePhoto"
                class="my-4"
                @image-changed="onStorePhotoChange" />
              <span v-else><img v-if="storePhoto" :src="storePhoto" /></span>
            </BaseDetailsTableRow>
          </tbody>
        </table>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';

import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import ImageUpload from '../common/ImageUpload.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseTableCellCheck from '../common/BaseTableCellCheck.vue';

import type { TraderSite } from '@/types/Traders';
import type { SelectDropdownItem } from '@/types/Base';
import { convertBlobToBase64 } from '@/utils/image';

const props = defineProps<{
  traderSite: TraderSite;
  isNew: boolean;
  mode: 'view' | 'edit';
  traderName?: string;
}>();

const emit = defineEmits(['close', 'save']);

const { dropdownVatLevels, dropdownCompanySites } = storeToRefs(useLookupsStore());
const { getVatLevelsLookup, getCompanySitesLookup } = useLookupsStore();
const modifiedData = ref<TraderSite>(deepCopy(props.traderSite));
const isModified = ref(false);
const selectedVatLevel = ref<SelectDropdownItem | null>(null);
const selectedCompanySite = ref<SelectDropdownItem | null>(null);
const storePhoto = computed<string | undefined>(() => {
  const raw = modifiedData.value?.storePhoto;
  return raw ? `data:image/jpeg;base64,${raw}` : undefined;
});
onMounted(async () => {
  await getVatLevelsLookup();
  await getCompanySitesLookup();
  initDropDowns();
});

function initDropDowns() {
  if (modifiedData.value && modifiedData.value.vatLevel) {
    const vatLevelValue = dropdownVatLevels.value.find(
      (item) => item.value == modifiedData.value?.vatLevel?.id
    );
    if (vatLevelValue) {
      selectedVatLevel.value = vatLevelValue;
      modifiedData.value.vatLevelId = modifiedData.value.vatLevel.id;
    }
  }
  if (modifiedData.value && modifiedData.value.companySite) {
    selectedCompanySite.value =
      dropdownCompanySites.value.find((x) => x.value === modifiedData.value?.companySite?.id) ||
      null;
    modifiedData.value.companySiteId = modifiedData.value.companySite?.id;
  }
}

function handleVatLevelSelection(item: SelectDropdownItem) {
  selectedVatLevel.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.vatLevelId = item ? item.value.toString() : undefined;
    modifiedData.value.vatLevel = item
      ? {
          id: item.value.toString(),
          name: item.name,
        }
      : undefined;
  }
}

function handleCompanySiteSelection(item: SelectDropdownItem) {
  selectedCompanySite.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.companySiteId = item ? item.value.toString() : '';
  }
}

function onStorePhotoChange(image: string) {
  if (!modifiedData.value) return;
  if (!image) {
    modifiedData.value.storePhoto = undefined;
    return;
  }
  if (image.startsWith('blob:')) {
    convertBlobToBase64(image)
      .then(
        (base64) =>
          (modifiedData.value!.storePhoto = base64.replace(/^data:image\/[a-zA-Z]+;base64,/, ''))
      )
      .catch((err) => console.error(err));
  } else if (image.startsWith('data:image/')) {
    modifiedData.value.storePhoto = image.replace(/^data:image\/[a-zA-Z]+;base64,/, '');
  }
}

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.companySiteId = newData.companySiteId || '';
    validationModel.value.vatLevel = newData.vatLevelId || '';
    validationModel.value.email = newData.email || '';
  },
  { deep: true }
);

const { t: $t } = useI18n({ useScope: 'global' });

const validationModel = ref({
  vatLevel: modifiedData.value?.vatLevelId || '',
  companySiteId: modifiedData.value?.companySiteId || '',
  email: modifiedData.value.email || '',
});
const rules = computed(() => ({
  vatLevel: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  companySiteId: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  email: { email: helpers.withMessage('Invalid email', email) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true, $scope: false });
</script>
